<template>
  <div
    v-if="!isLoading && !hasPlan && showBanner"
    class="banner-container"
    :class="{ show: showBanner }"
    :style="buildComponentStyles"
    @click="goToLink"
  >
    <div class="banner">
      <img id="banner-image" class="banner-image" :src="imageUrl" :alt="bannerAlt" />
      <button
        class="close-button"
        type="button"
        aria-label="Close banner"
        @click.stop="close"
      ></button>
    </div>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';

export default {
  data() {
    return {
      link: null,
      imageUrl: null,
      backgroundColor: null,
      isVisible: null,
      hasClosed: false,
      delayToShowBanner: 150000,
      idInterval: null,
      pagesToExclude: ['plans', 'planos', 'abos', 'planes', 'purchase', 'kauf', 'compra'],
      landingsToExclude: ['exclusiveoffer35off'],
      isLoading: true,
      cookieNameClosed: 'vexels_banner_closed',
      strategyCookieName: 'vx.strategy',
      timerCookieName: 'vx.timerBannerBB',
      cookieExpiryDays: 3
    };
  },
  computed: {
    ...mapGetters('user', ['isLoggedIn']),
    buildComponentStyles() {
      return `
        --background-color: ${this.backgroundColor};
        --background-image: url("${this.imageUrl}");
      `;
    },
    showBanner() {
      return !this.hasClosed && this.isVisible;
    },
    bannerAlt() {
      return 'Banner promocional';
    },
    hasPlan() {
      return (this.isLoggedIn && this.isLoggedIn.idPlan !== 1) || this.$cookies.get('vx.hasPlan');
    }
  },
  watch: {
    $route() {
      this.setVisibleBanner();
    },
    '$auth.user.isLogged'() {
      this.isVisible = !this.hasPlan;
    }
  },
  async created() {
    try {
      const {
        data: {
          data: { link, image, background_color, title }
        }
      } = await this.$axiosUtils.get('configurations/?config=banner_b', {
        headers: {
          'Accept-Language': this.getLanguageFromSubdomain()
        }
      });
      this.link = link || this.link;
      this.imageUrl = image || this.imageUrl;
      this.backgroundColor = background_color || this.backgroundColor;
      if (title) {
        this.bannerTitle = title;
      }
    } catch (error) {
      console.error('Error on get data for BannerB', error);
    }
  },
  mounted() {
    // Do not initialize if user has plan or page must hide banner
    if (this.hasPlan || this.mustHideFromPage()) {
      this.isVisible = false;
      this.isLoading = false;
      return;
    }

    // Respect closed banner cookie (any value means closed)
    if (this.$cookies.get(this.cookieNameClosed)) {
      this.isVisible = false;
      this.isLoading = false;
      return;
    }

    // Respect strategy cookie
    if (this.$cookies.get(this.strategyCookieName)) {
      this.isVisible = false;
      this.isLoading = false;
      return;
    }

    const timerCookie = this.$cookies.get(this.timerCookieName);
    if (!timerCookie) {
      const startingMs = new Date().getTime();
      const endingMs = startingMs + this.delayToShowBanner;
      // Store timer cookie for the same expiry as GTM snippet (3 days)
      this.$cookies.set(this.timerCookieName, endingMs, {
        path: '/',
        maxAge: this.cookieExpiryDays * 24 * 60 * 60
      });
      this.idInterval = setInterval(this.setVisibleAfterDelay, 1000);
    } else {
      this.setVisibleAfterDelay();
    }
  },
  methods: {
    setLoaded() {
      this.isLoading = false;
    },
    setVisibleBanner() {
      if (!this.mustHideFromPage() && !this.hasClosed) {
        this.setVisibleAfterDelay();
      } else {
        this.isVisible = false;
      }
    },
    setVisibleAfterDelay() {
      // Do not show if closed or strategy cookie is present
      if (this.$cookies.get(this.cookieNameClosed) || this.$cookies.get(this.strategyCookieName)) {
        this.isVisible = false;
        this.setLoaded();
        if (this.idInterval) clearInterval(this.idInterval);
        return;
      }

      const bannerEndTimerMs = this.$cookies.get(this.timerCookieName);
      const nowMs = new Date().getTime();
      const differenceMs = nowMs - bannerEndTimerMs;
      if (differenceMs > 0) {
        if (this.idInterval) clearInterval(this.idInterval);
        this.isVisible = true;
        this.setLoaded();
      }
    },
    goToLink(event) {
      const id = event.target.id;
      if (id !== 'close-banner') {
        this.$dataLayerPush('click_banner_b');
        if (this.link) {
          window.open(this.link, '_blank');
        }
      }
    },
    close() {
      this.hasClosed = true;
      this.isVisible = false;
      this.setLoaded();
      // Mirror GTM behavior: set closed cookie for 3 days
      this.$cookies.set(this.cookieNameClosed, 'true', {
        path: '/',
        maxAge: this.cookieExpiryDays * 24 * 60 * 60
      });
      if (this.idInterval) {
        clearInterval(this.idInterval);
        this.idInterval = null;
      }
    },
    mustHideFromPage() {
      const page = window.location.href;
      const pagesToExclude = this.pagesToExclude.concat(this.landingsExcluded);
      for (var i = 0; i < pagesToExclude.length; i++) {
        if (page.indexOf(pagesToExclude[i]) > -1) return true;
      }
      return false;
    },
    getLanguageFromSubdomain() {
      const hostname = window.location.hostname;
      const subdomain = hostname.split('.')[0];

      switch (subdomain) {
        case 'es':
          return 'es';
        case 'de':
          return 'de';
        case 'br':
          return 'pt';
        default:
          return 'en';
      }
    }
  }
};
</script>
<style lang="scss" scoped>
.banner-container {
  position: fixed;
  bottom: 20px;
  left: -220px;
  z-index: 9999;
  transition: left 0.5s ease-in-out;
  cursor: pointer;
}

.banner-container.show {
  left: 20px;
}

.banner {
  width: 200px;
  height: 200px;
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  cursor: pointer;
  background-color: var(--background-color);
  background-image: var(--background-image);
  background-size: cover;
  background-position: center;
}

.banner-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  cursor: pointer;
  display: block;
  transition: transform 0.2s ease-in-out;
}

.banner-image:hover {
  transform: scale(1.02);
}

.close-button {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: bold;
  color: #333;
  z-index: 10;
  transition: background-color 0.2s;
}

.close-button:hover {
  background: rgba(255, 255, 255, 1);
}

.close-button::before,
.close-button::after {
  content: '';
  position: absolute;
  width: 12px;
  height: 2px;
  background: #333;
  transform: rotate(45deg);
}

.close-button::after {
  transform: rotate(-45deg);
}
</style>
